// Phase 25 iter-5c — gating hook compartido.
//
// Consolida a lóxica "podes premer este botón?" nun lugar único para
// non duplicar checks en cada páxina. Combina:
//   • user.roles[]           — Phase 12
//   • user.farm_id           — true cando o FARMER xa creou a Farm
//   • user.public_key        — true cando o VET subiu a chave P-256
//   • useKycStatus()         — DIDIT VERIFIED/PENDING/etc.
//   • user.email_verified    — para algunhas accións bloqueantes
//
// Cada flag devolve, ademais de `allowed: boolean`, un `reason` curto
// localizable que a UI pode amosar inline sen tener que coñecer a
// lóxica interna.

import { useMemo } from 'react';

import { useAuthContext } from '../context/AuthContext';
import { useKycStatus } from './useKycStatus';

export type GateReason =
  | 'NEED_LOGIN'
  | 'NEED_EMAIL_VERIFIED'
  | 'NEED_KYC'
  | 'NEED_ROLE_FARMER'
  | 'NEED_ROLE_BUYER'
  | 'NEED_ROLE_VET'
  | 'NEED_ROLE_TRANSPORTISTA'
  | 'NEED_FARM'
  | 'NEED_PUBLIC_KEY'
  | 'NEED_VET_LICENSE_VERIFIED';

export interface GateFlag {
  allowed: boolean;
  reasons: GateReason[];
}

export interface GateAPI {
  isLoggedIn: boolean;
  canReserve: GateFlag;
  canSell: GateFlag;
  canSignAsVet: GateFlag;
  canDocumentTransport: GateFlag;
}

const REASON_COPY: Record<GateReason, string> = {
  NEED_LOGIN: 'Inicia sesión para continuar.',
  NEED_EMAIL_VERIFIED: 'Verifica o teu email primeiro.',
  NEED_KYC: 'Completa a verificación de identidade (KYC).',
  NEED_ROLE_FARMER: 'Esta acción require o rol Gandeiro/a.',
  NEED_ROLE_BUYER: 'Esta acción require o rol Comprador/a.',
  NEED_ROLE_VET: 'Esta acción require o rol Veterinario/a.',
  NEED_ROLE_TRANSPORTISTA: 'Esta acción require o rol Transportista.',
  NEED_FARM: 'Crea a túa explotación antes de continuar.',
  NEED_PUBLIC_KEY: 'Sube a túa chave ECDSA dende a app móbil.',
  NEED_VET_LICENSE_VERIFIED: 'O teu número de colexiado aínda non está verificado.',
};

export function describeGateReason(reason: GateReason): string {
  return REASON_COPY[reason];
}

export function useGate(): GateAPI {
  const { user, accessToken } = useAuthContext();
  const { status: kyc } = useKycStatus();

  return useMemo<GateAPI>(() => {
    const isLoggedIn = !!accessToken && !!user;
    if (!isLoggedIn) {
      const r: GateReason[] = ['NEED_LOGIN'];
      return {
        isLoggedIn,
        canReserve: { allowed: false, reasons: r },
        canSell: { allowed: false, reasons: r },
        canSignAsVet: { allowed: false, reasons: r },
        canDocumentTransport: { allowed: false, reasons: r },
      };
    }
    const roles = user.roles;
    const kycVerified = kyc?.status === 'VERIFIED';
    const kycRequired = kyc?.required === true;

    function evaluate(
      role: 'FARMER' | 'BUYER' | 'VET' | 'TRANSPORTISTA',
      extra: (reasons: GateReason[]) => void,
    ): GateFlag {
      const reasons: GateReason[] = [];
      if (!roles.includes(role)) {
        reasons.push(
          role === 'FARMER'
            ? 'NEED_ROLE_FARMER'
            : role === 'BUYER'
              ? 'NEED_ROLE_BUYER'
              : role === 'VET'
                ? 'NEED_ROLE_VET'
                : 'NEED_ROLE_TRANSPORTISTA',
        );
      }
      if (kycRequired && !kycVerified) reasons.push('NEED_KYC');
      extra(reasons);
      return { allowed: reasons.length === 0, reasons };
    }

    return {
      isLoggedIn,
      // Reservar: BUYER (ou FARMER se ten o segundo rol) + KYC + email
      canReserve: evaluate('BUYER', (r) => {
        if (!user.email_verified) r.push('NEED_EMAIL_VERIFIED');
      }),
      // Vender: FARMER + Farm rexistrada + email
      canSell: evaluate('FARMER', (r) => {
        if (!user.farm_id) r.push('NEED_FARM');
        if (!user.email_verified) r.push('NEED_EMAIL_VERIFIED');
      }),
      // Asinar como VET: VET + public_key + email + KYC
      canSignAsVet: evaluate('VET', (r) => {
        if (!user.public_key) r.push('NEED_PUBLIC_KEY');
        if (!user.email_verified) r.push('NEED_EMAIL_VERIFIED');
      }),
      // Documentar transporte: TRANSPORTISTA + email
      canDocumentTransport: evaluate('TRANSPORTISTA', (r) => {
        if (!user.email_verified) r.push('NEED_EMAIL_VERIFIED');
      }),
    };
  }, [user, accessToken, kyc]);
}
